// =====================================================================
// WeddingOS – /bar  (Barkeeper-Screen)
// =====================================================================
// Fokussierter Vollbild-Screen – kein Menü, kein Burger.
// • Token aus URL (?token=...) oder sessionStorage
// • Bestellungen live in Echtzeit
// • Status: Bestellt → (ein Klick) → Fertig
// • Sticky Header zeigt offene Bestellungen als Badge
// =====================================================================

import { useState, useEffect, useCallback } from "react";
import { supabase, barUpdateStatus, barAddOrder, barGetTables, barGetMenu, barDeleteOrder, barSetAvailable } from "../../services/supabase";
import { realtime } from "../../services/supabase";
import BarIntro from "../../components/BarIntro";
import { tableDisplay } from "../../lib/guestSession";
import "../../styles/theme.css";

// Tischnummern numerisch sortieren wenn möglich, sonst alphabetisch. null/"" = ganz unten.
const byTable = (a, b) => {
  if (a === b) return 0;
  if (a == null || a === "") return 1;
  if (b == null || b === "") return -1;
  const na = Number(a), nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b));
};
const tableOf = (o) => o.table_number ?? o.guest?.table_number ?? null;

const BAR_TOKEN_KEY = "wos_bar_token";
const BAR_INTRO_KEY = "wos_bar_intro_seen";

// Nur zwei Zustände nach außen: Bestellt und Fertig.
// (Enum behält ordered/preparing/ready/delivered; preparing zählt als „Bestellt",
//  delivered als „Fertig" – damit Altbestellungen sauber einsortiert bleiben.)
const isDone = (s) => s === "ready" || s === "delivered";

const STATUS_CFG = {
  ordered:   { label: "Bestellt", color: "var(--ink-muted)", bg: "var(--surface)",     dot: "var(--line)" },
  preparing: { label: "Bestellt", color: "var(--ink-muted)", bg: "var(--surface)",     dot: "var(--line)" },
  ready:     { label: "Fertig",   color: "var(--forest)",    bg: "var(--forest-soft)", dot: "var(--forest)" },
  delivered: { label: "Fertig",   color: "var(--forest)",    bg: "var(--forest-soft)", dot: "var(--forest)" },
};

// Ein Klick: Bestellt → Fertig. Fertig ist Endzustand.
const NEXT = {
  ordered:   { label: "Als fertig markieren ✓", next: "ready" },
  preparing: { label: "Als fertig markieren ✓", next: "ready" },
  ready:     null,
  delivered: null,
};

export default function BarOrders() {
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) sessionStorage.setItem(BAR_TOKEN_KEY, t);
    return t ?? sessionStorage.getItem(BAR_TOKEN_KEY) ?? null;
  });

  // Erst-Öffnen-Intro (pro Token gemerkt)
  const [showIntro, setShowIntro] = useState(
    () => !!token && !localStorage.getItem(`${BAR_INTRO_KEY}_${token}`)
  );

  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [updating,   setUpdating]   = useState(new Set());
  const [weddingId,  setWeddingId]  = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [error,      setError]      = useState(null);

  // Kellner-Bestellung aufnehmen
  const [showAdd,       setShowAdd]       = useState(false);
  const [showSortiment, setShowSortiment] = useState(false);
  const [tables,        setTables]        = useState([]);
  const [menu,          setMenu]          = useState([]);

  // Bestellungen laden (mit Gast-Join + Tisch aus bar_get_orders)
  const loadOrders = useCallback(async () => {
    if (!token) return;
    try {
      const { data, error: rpcErr } = await supabase.rpc("bar_get_orders", { p_token: token });
      if (rpcErr) throw rpcErr;
      const rows = data ?? [];
      setOrders(rows);
      if (rows[0]?.wedding_id) setWeddingId(rows[0].wedding_id);
    } catch (err) {
      setError(err.message?.includes("invalid_token")
        ? "Dieser Barkeeper-Link ist nicht gültig."
        : "Bestellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Realtime. INSERT liefert die Roh-Zeile ohne Gast-Join → für Gast-Bestellungen
  // (Name/Tisch fehlen) neu laden statt roh anzuhängen. Status/Storno lokal patchen.
  useEffect(() => {
    if (!weddingId) return;
    return realtime.drinkOrders(weddingId, (p) => {
      if (p.eventType === "INSERT") {
        loadOrders(); // holt die neue Bestellung angereichert (Name + Tisch)
        return;
      }
      setOrders((prev) => {
        if (p.eventType === "UPDATE") return prev.map((o) => o.id === p.new.id ? { ...o, ...p.new } : o);
        if (p.eventType === "DELETE") return prev.filter((o) => o.id !== p.old.id);
        return prev;
      });
    });
  }, [weddingId, loadOrders]);

  const cancelOrder = useCallback(async (order) => {
    if (!token) return;
    if (!window.confirm(`Bestellung „${order.quantity > 1 ? order.quantity + "× " : ""}${order.drink_name}" wirklich stornieren?`)) return;
    setOrders((prev) => prev.filter((o) => o.id !== order.id)); // optimistisch
    try {
      await barDeleteOrder({ token, orderId: order.id });
    } catch {
      setError("Stornieren fehlgeschlagen.");
      loadOrders(); // Zustand zurückholen
    }
  }, [token, loadOrders]);

  // Tische + Karte für die Kellner-Bestellung laden (token-basiert, kein weddingId nötig)
  const loadMenu = useCallback(() => {
    if (!token) return;
    barGetMenu(token).then(setMenu).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    barGetTables(token).then(setTables).catch(() => {});
    loadMenu();
  }, [token, loadMenu]);

  // Realtime: Verfügbarkeit über mehrere Bar-Geräte synchron halten
  useEffect(() => {
    if (!weddingId) return;
    return realtime.drinkMenu(weddingId, () => loadMenu());
  }, [weddingId, loadMenu]);

  // Getränk aus-/einschalten ("Ausgetrunken")
  const toggleAvailable = useCallback(async (item, available) => {
    if (!token) return;
    setMenu((prev) => prev.map((i) => i.id === item.id ? { ...i, available } : i)); // optimistisch
    try {
      await barSetAvailable({ token, itemId: item.id, available });
    } catch {
      setMenu((prev) => prev.map((i) => i.id === item.id ? { ...i, available: !available } : i));
      setError("Konnte Verfügbarkeit nicht ändern.");
    }
  }, [token]);

  const addOrder = useCallback(async ({ tableNumber, drinkName, quantity, note }) => {
    if (!token) return;
    const row = await barAddOrder({ token, tableNumber, drinkName, quantity, note });
    setOrders((prev) => prev.some((o) => o.id === row.id) ? prev : [row, ...prev]);
    if (row?.wedding_id && !weddingId) setWeddingId(row.wedding_id); // aktiviert Realtime falls noch keine Bestellungen da waren
  }, [token, weddingId]);

  const advance = useCallback(async (order) => {
    const action = NEXT[order.status];
    if (!action || !token) return;
    setUpdating((s) => new Set(s).add(order.id));
    try {
      await barUpdateStatus({ token, orderId: order.id, status: action.next });
      setOrders((prev) => prev.map((o) => o.id === order.id ? { ...o, status: action.next } : o));
    } catch {
      setError("Status-Update fehlgeschlagen.");
    } finally {
      setUpdating((s) => { const n = new Set(s); n.delete(order.id); return n; });
    }
  }, [token]);

  if (!token) return <AccessDenied />;
  if (showIntro) return (
    <BarIntro onDone={() => {
      try { localStorage.setItem(`${BAR_INTRO_KEY}_${token}`, "1"); } catch { /* Private-Mode */ }
      setShowIntro(false);
    }} />
  );

  const active   = orders.filter((o) => !isDone(o.status));
  const archived = orders.filter((o) => isDone(o.status));

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerRow}>
          <div>
            <p style={styles.monogram}>unsereHochzeits.app</p>
            <h1 style={styles.title}>
              Bar
              {active.length > 0 && (
                <span style={styles.countBadge}>{active.length}</span>
              )}
            </h1>
          </div>
          <LiveDot />
        </div>
        {error && (
          <div className="wos-error" style={{ marginTop: 10 }}>
            {error}
            <button onClick={() => setError(null)} style={styles.dismissBtn}>×</button>
          </div>
        )}
      </header>

      {/* ── Content ── */}
      <main style={styles.content}>
        {showAdd ? (
          <WaiterSheet
            tables={tables}
            menu={menu}
            onSubmit={addOrder}
            onClose={() => setShowAdd(false)}
          />
        ) : showSortiment ? (
          <SortimentSheet
            menu={menu}
            onToggle={toggleAvailable}
            onClose={() => setShowSortiment(false)}
          />
        ) : loading ? (
          <Skeleton rows={3} />
        ) : (
          <>
            {/* Kellner: Bestellung aufnehmen + Sortiment verwalten */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button onClick={() => setShowAdd(true)} style={{ ...styles.addBtn, flex: 1, marginBottom: 0 }}>
                ＋ Bestellung aufnehmen
              </button>
              <button onClick={() => setShowSortiment(true)} style={styles.sortimentBtn}>
                Sortiment
              </button>
            </div>

            {active.length === 0 && archived.length === 0 ? (
              <Empty />
            ) : (
              <>
                {/* Offene Bestellungen – pro Tisch gruppiert, ältestes oben */}
                {active.length > 0 && (
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "var(--ink-muted)", textTransform: "uppercase" }}>
                        Bestellt
                      </span>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--line)", flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: "var(--ink-subtle)" }}>{active.length}</span>
                      <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
                    </div>
                    {groupByTable(active).map(([table, list]) => (
                      <div key={table ?? "__none"} style={{ marginBottom: 20 }}>
                        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", color: "var(--ink)", margin: "0 0 8px" }}>
                          {table ? tableDisplay(table, list[0]?.table_label) : "Ohne Tisch"}
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {list.map((o) => (
                            <OrderCard
                              key={o.id}
                              order={o}
                              busy={updating.has(o.id)}
                              onAdvance={() => advance(o)}
                              onCancel={() => cancelOrder(o)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

            {/* Archiv */}
            {archived.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={() => setShowArchive((v) => !v)}
                  style={styles.archiveToggle}
                >
                  <span style={{ transform: showArchive ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▶</span>
                  Fertig ({archived.length})
                </button>
                {showArchive && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                    {archived.map((o) => (
                      <OrderCard key={o.id} order={o} busy={false} onAdvance={null} dimmed />
                    ))}
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── groupByTable: aktive Bestellungen pro Tisch, ältestes zuerst ──────
function groupByTable(orders) {
  const groups = {};
  for (const o of orders) {
    const k = tableOf(o) ?? "__none";
    (groups[k] = groups[k] ?? []).push(o);
  }
  for (const list of Object.values(groups)) {
    list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }
  return Object.entries(groups)
    .map(([k, list]) => [k === "__none" ? null : k, list])
    .sort((a, b) => byTable(a[0], b[0]));
}

// ── WaiterSheet: Kellner nimmt Bestellung pro Tisch auf ───────────────
// Ablauf: Tisch wählen → Gruppe aufklappen → pro Zeile Menge + ✓ (bleibt offen
// für weitere Bestellungen, damit gleiche Getränke am Tisch schnell durchgehen).
function WaiterSheet({ tables, menu, onSubmit, onClose }) {
  const [table, setTable] = useState(null);
  const [toast, setToast] = useState(null); // { text } nach erfolgreicher Bestellung
  const [err,   setErr]   = useState(null);

  // tables = [{ table_number, table_label }]; Freitext-Walk-in ist eine Nummer, die nicht in der Liste steht
  const known = tables.some((t) => t.table_number === table);
  const labelOf = (num) => tables.find((t) => t.table_number === num)?.table_label;

  const grouped = menu.reduce((acc, i) => {
    const k = i.category || "Sonstiges";
    (acc[k] = acc[k] ?? []).push(i);
    return acc;
  }, {});

  const order = async (drink, qty) => {
    if (!table) { setErr("Zuerst einen Tisch wählen."); return; }
    setErr(null);
    await onSubmit({ tableNumber: table, drinkName: drink.name, quantity: qty });
    setToast({ text: `${qty}× ${drink.name} → ${tableDisplay(table, labelOf(table))} ✓` });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", margin: 0 }}>Bestellung aufnehmen</h2>
        <button onClick={onClose} style={styles.sheetClose}>Fertig</button>
      </div>

      {/* Tisch wählen (bleibt oben sichtbar) – Chips aus Gästeliste + Freitext für Walk-in */}
      <p style={styles.sheetLabel}>Tisch</p>
      {tables.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          {tables.map((t) => (
            <button key={t.table_number} onClick={() => setTable(t.table_number)}
              style={{ ...styles.chip, ...(table === t.table_number ? styles.chipActive : {}) }}>
              {t.table_label ? `${t.table_number} – ${t.table_label}` : t.table_number}
            </button>
          ))}
        </div>
      )}
      <input
        className="wos-input"
        placeholder={tables.length ? "Andere Tisch-Nr. (Walk-in)" : "Tisch-Nr. eingeben"}
        value={known ? "" : (table ?? "")}
        onChange={(e) => setTable(e.target.value.trim() || null)}
        maxLength={20}
        style={{ marginBottom: 8, ...(table && !known ? { borderColor: "var(--forest)" } : {}) }}
      />

      {toast && (
        <div style={{ padding: "9px 12px", background: "var(--forest-soft)", border: "1px solid var(--forest)",
          borderRadius: "var(--radius-sm)", color: "var(--forest)", fontSize: 13, fontWeight: 600, margin: "8px 0" }}>
          {toast.text}
        </div>
      )}
      {err && <div className="wos-error" style={{ margin: "8px 0" }}>{err}</div>}

      {/* Getränk – nach Gruppe, eingeklappt; Menge + ✓ direkt in der Zeile */}
      <p style={{ ...styles.sheetLabel, marginTop: 16 }}>Getränk</p>
      <div style={{ marginBottom: 12 }}>
        {Object.keys(grouped).length === 0 ? (
          <p className="wos-meta" style={{ fontSize: 13 }}>Keine Getränke in der Karte.</p>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <MenuGroup key={cat} category={cat} items={items} tableChosen={!!table} onOrder={order} />
          ))
        )}
      </div>
    </div>
  );
}

function MenuGroup({ category, items, tableChosen, onOrder }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 8 }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%",
          background: "none", border: "none", padding: "6px 0", cursor: "pointer", textAlign: "left" }}>
        <span className="wos-eyebrow">{category}</span>
        <span style={{ fontSize: 11, color: "var(--ink-subtle)" }}>({items.length})</span>
        <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
        <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", color: "var(--ink-muted)" }}>⌄</span>
      </button>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
          {items.map((i) => (
            <DrinkRow key={i.id} drink={i} tableChosen={tableChosen} onOrder={onOrder} />
          ))}
        </div>
      )}
    </div>
  );
}

// Eine Getränkezeile: Name · − Menge + · ✓  (alles in einer Zeile)
function DrinkRow({ drink, tableChosen, onOrder }) {
  const [qty,  setQty]  = useState(1);
  const [busy, setBusy] = useState(false);
  const step = (n) => setQty((q) => Math.max(1, Math.min(99, q + n)));

  const confirm = async () => {
    setBusy(true);
    try {
      await onOrder(drink, qty);
      setQty(1); // Zeile zurücksetzen für nächste Bestellung
    } catch {
      /* Fehler zeigt WaiterSheet */
    } finally {
      setBusy(false);
    }
  };

  const sBtn = {
    width: 32, height: 32, borderRadius: "var(--radius-sm)", border: "1px solid var(--line)",
    background: "var(--bg-elevated)", color: "var(--ink)", fontSize: 17, lineHeight: 1,
    cursor: "pointer", fontFamily: "var(--font-body)", flexShrink: 0,
  };

  if (drink.available === false) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        padding: "8px 10px", background: "var(--surface)", border: "1px solid var(--line)",
        borderRadius: "var(--radius-md)", opacity: 0.55 }}>
        <span style={{ flex: 1, fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", color: "var(--ink-muted)", textDecoration: "line-through" }}>
          {drink.name}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
          color: "var(--ink-subtle)", flexShrink: 0 }}>Ausgetrunken</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
      background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)" }}>
      <span style={{ flex: 1, fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {drink.name}
      </span>
      <button type="button" aria-label="weniger" onClick={() => step(-1)} disabled={qty <= 1}
        style={{ ...sBtn, opacity: qty <= 1 ? 0.4 : 1 }}>−</button>
      <span style={{ minWidth: 20, textAlign: "center", fontSize: 15, fontWeight: 700 }}>{qty}</span>
      <button type="button" aria-label="mehr" onClick={() => step(1)} disabled={qty >= 99}
        style={sBtn}>+</button>
      <button type="button" onClick={confirm} disabled={!tableChosen || busy} aria-label="bestellen"
        style={{ width: 40, height: 32, borderRadius: "var(--radius-sm)", border: "none",
          background: tableChosen ? "var(--forest)" : "var(--line)", color: "#fff", fontSize: 16,
          cursor: tableChosen && !busy ? "pointer" : "not-allowed", opacity: busy ? 0.6 : 1, flexShrink: 0 }}>
        {busy ? "…" : "✓"}
      </button>
    </div>
  );
}

// ── OrderCard ────────────────────────────────────────────────────────

function OrderCard({ order, busy, onAdvance, onCancel, dimmed }) {
  const cfg    = STATUS_CFG[order.status] ?? STATUS_CFG.ordered;
  const action = NEXT[order.status];
  const time   = formatTime(order.created_at);
  const isReady = order.status === "ready";

  return (
    <div style={{
      background:   cfg.bg,
      border:       `1px solid ${isReady ? "var(--forest)" : "var(--line)"}`,
      borderRadius: "var(--radius-md)",
      padding:      "14px 16px",
      opacity:      dimmed ? 0.55 : 1,
      transition:   "background 0.4s",
    }}>
      {/* Getränk + Anmerkung */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
            {order.quantity > 1 && <span style={{ color: "var(--accent)" }}>{order.quantity}× </span>}
            {order.drink_name}
          </p>
          {order.note && (
            <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--ink-muted)", fontStyle: "italic" }}>
              {order.note}
            </p>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 8 }}>
          <span style={{ fontSize: 11, color: "var(--ink-subtle)" }}>{time}</span>
          {onCancel && (
            <button onClick={onCancel} aria-label="Bestellung stornieren" title="Stornieren"
              style={{ width: 24, height: 24, borderRadius: "var(--radius-sm)", border: "1px solid var(--line)",
                background: "var(--surface)", color: "var(--ink-muted)", fontSize: 15, lineHeight: 1,
                cursor: "pointer", flexShrink: 0 }}>×</button>
          )}
        </div>
      </div>

      {/* Gast – nur wenn Name da (Tisch steht im Gruppen-Header). Kellner-Bestellung hat keinen Gast. */}
      {order.guest?.name && (
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "7px 10px", background: "rgba(0,0,0,0.04)", borderRadius: "var(--radius-sm)",
        marginBottom: action ? 12 : 0,
      }}>
        <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>
          {order.guest.name}
        </span>
      </div>
      )}

      {/* Action-Button */}
      {action && onAdvance && (
        <button
          onClick={onAdvance}
          disabled={busy}
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            gap:            8,
            width:          "100%",
            padding:        "11px 16px",
            background:     isReady ? "var(--forest)" : "var(--accent)",
            color:          "#fff",
            border:         "none",
            borderRadius:   "var(--radius-sm)",
            fontSize:       14,
            fontWeight:     500,
            cursor:         busy ? "wait" : "pointer",
            opacity:        busy ? 0.7 : 1,
            fontFamily:     "var(--font-body)",
            transition:     "opacity 0.15s",
          }}
        >
          {busy
            ? <><span className="wos-spinner" style={{ width: 14, height: 14 }} />Aktualisiert …</>
            : action.label}
        </button>
      )}
    </div>
  );
}

// ── SortimentSheet: Bar schaltet Getränke aus/ein ("Ausgetrunken") ────
function SortimentSheet({ menu, onToggle, onClose }) {
  const grouped = menu.reduce((acc, i) => {
    const k = i.category || "Sonstiges";
    (acc[k] = acc[k] ?? []).push(i);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", margin: 0 }}>Sortiment</h2>
        <button onClick={onClose} style={styles.sheetClose}>Fertig</button>
      </div>
      <p className="wos-meta" style={{ fontSize: 13, margin: "0 0 18px" }}>
        Ist ein Getränk aus? Ausschalten – Gäste sehen es dann ausgegraut als „Ausgetrunken" und können es nicht bestellen.
      </p>

      {Object.keys(grouped).length === 0 ? (
        <p className="wos-meta" style={{ fontSize: 13 }}>Keine Getränke in der Karte.</p>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span className="wos-eyebrow">{cat}</span>
              <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map((i) => <SortimentRow key={i.id} drink={i} onToggle={onToggle} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function SortimentRow({ drink, onToggle }) {
  const available = drink.available !== false;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
      background: available ? "var(--bg-elevated)" : "var(--surface)",
      border: "1px solid var(--line)", borderRadius: "var(--radius-md)", opacity: available ? 1 : 0.7 }}>
      <span style={{ flex: 1, fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
        whiteSpace: "nowrap", color: available ? "var(--ink)" : "var(--ink-muted)",
        textDecoration: available ? "none" : "line-through" }}>
        {drink.name}
      </span>
      {!available && (
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
          color: "var(--ink-subtle)", flexShrink: 0 }}>Ausgetrunken</span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={available}
        aria-label={available ? `${drink.name} als ausgetrunken markieren` : `${drink.name} wieder verfügbar machen`}
        onClick={() => onToggle(drink, !available)}
        style={{
          position: "relative", width: 46, height: 26, borderRadius: 999, border: "none",
          background: available ? "var(--forest)" : "var(--line)", cursor: "pointer",
          flexShrink: 0, transition: "background 0.2s", padding: 0,
        }}
      >
        <span style={{ position: "absolute", top: 3, left: available ? 23 : 3, width: 20, height: 20,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s",
          boxShadow: "0 1px 2px rgba(0,0,0,0.25)" }} />
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function LiveDot() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--forest)", boxShadow: "0 0 0 2px var(--forest-soft)" }} />
      <span style={{ fontSize: 10, color: "var(--ink-subtle)", letterSpacing: "0.12em" }}>LIVE</span>
    </div>
  );
}

function Skeleton({ rows }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ height: 96, borderRadius: "var(--radius-md)", background: "var(--surface)", opacity: 0.5 }} />
      ))}
    </div>
  );
}

function Empty() {
  return (
    <div style={{ textAlign: "center", paddingTop: 80 }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", color: "var(--ink-muted)" }}>
        Keine offenen Bestellungen
      </p>
      <p style={{ fontSize: 13, color: "var(--ink-subtle)", marginTop: 8 }}>
        Neue erscheinen automatisch.
      </p>
    </div>
  );
}

function AccessDenied() {
  return (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)", fontFamily: "var(--font-body)" }}>
      <div className="wos-card" style={{ maxWidth: 340, textAlign: "center" }}>
        <p style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem" }}>Kein Zugang</p>
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-muted)" }}>
          Öffne den Barkeeper-Link, den du vom Brautpaar erhalten hast.
        </p>
      </div>
    </div>
  );
}

function formatTime(iso) {
  try { return new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso)); }
  catch { return ""; }
}

// ── Styles ────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight:     "100dvh",
    background:    "var(--bg)",
    fontFamily:    "var(--font-body)",
    color:         "var(--ink)",
    display:       "flex",
    flexDirection: "column",
    maxWidth:      640,
    margin:        "0 auto",
    width:         "100%",
  },
  header: {
    padding:      "18px 20px 14px",
    borderBottom: "1px solid var(--line)",
    background:   "var(--bg-elevated)",
    position:     "sticky",
    top:          0,
    zIndex:       10,
  },
  headerRow: {
    display:        "flex",
    justifyContent: "space-between",
    alignItems:     "flex-start",
  },
  monogram: {
    fontFamily:    "var(--font-display)",
    fontStyle:     "italic",
    fontSize:      11,
    letterSpacing: "0.25em",
    color:         "var(--accent)",
    margin:        "0 0 4px",
  },
  title: {
    fontFamily:  "var(--font-display)",
    fontSize:    "1.8rem",
    fontWeight:  500,
    margin:      0,
    lineHeight:  1,
    display:     "flex",
    alignItems:  "center",
    gap:         10,
  },
  countBadge: {
    background:   "var(--accent)",
    color:        "#fff",
    fontSize:     12,
    fontWeight:   700,
    borderRadius: "999px",
    padding:      "2px 9px",
    lineHeight:   "20px",
    fontFamily:   "var(--font-body)",
  },
  content: {
    flex:    1,
    padding: "20px 20px 60px",
  },
  archiveToggle: {
    display: "flex", alignItems: "center", gap: 8,
    background: "none", border: "none", cursor: "pointer",
    color: "var(--ink-muted)", fontSize: 13, letterSpacing: "0.1em",
    fontFamily: "var(--font-body)", padding: 0,
  },
  dismissBtn: {
    background: "none", border: "none", cursor: "pointer",
    float: "right", color: "var(--accent-deep)", fontWeight: 700,
  },
  addBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    width: "100%", padding: "13px 16px", marginBottom: 24,
    background: "var(--forest)", color: "#fff", border: "none",
    borderRadius: "var(--radius-md)", fontSize: 15, fontWeight: 600,
    cursor: "pointer", fontFamily: "var(--font-body)",
  },
  sortimentBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "13px 16px", background: "var(--bg-elevated)", color: "var(--ink)",
    border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
    fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
    flexShrink: 0, whiteSpace: "nowrap",
  },
  sheetClose: {
    background: "none", border: "none", cursor: "pointer",
    color: "var(--ink-muted)", fontSize: 14, fontFamily: "var(--font-body)",
  },
  sheetLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
    textTransform: "uppercase", color: "var(--ink-muted)", margin: "0 0 8px",
  },
  chip: {
    minWidth: 44, padding: "10px 16px", background: "var(--bg-elevated)",
    border: "1px solid var(--line)", borderRadius: "999px",
    fontSize: 15, fontWeight: 600, cursor: "pointer",
    color: "var(--ink)", fontFamily: "var(--font-body)",
  },
  chipActive: {
    background: "var(--forest)", color: "#fff", border: "1px solid var(--forest)",
  },
  sheetSubmit: {
    width: "100%", padding: "14px 16px", background: "var(--accent)",
    color: "#fff", border: "none", borderRadius: "var(--radius-md)",
    fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
  },
};
